import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import { catchError, switchMap, takeUntil, tap } from 'rxjs/operators';
import { BoatService } from '../services/boat.service';
import { getBoatImageUrl } from '../utils/image-url.util';

@Component({ selector: 'app-hasil-cari', templateUrl: './hasil-cari.component.html', styleUrls: ['./hasil-cari.component.css'] })
export class HasilCariComponent implements OnInit, OnDestroy {
  displayedBoats: any[] = [];
  totalItems = 0;
  totalPages = 0;
  currentPage = 1;
  readonly pageSize = 12;
  loading = false;
  errorMessage = '';
  filtersOpen = false;
  params: any = {};
  selectedCategories: string[] = [];
  selectedDestinations: string[] = [];
  minPrice: number | null = null;
  maxPrice: number | null = null;
  sort = 'recommended';
  activeTab = 'all';
  readonly categories = ['Deluxe Boat', 'Speed Boat', 'VIP Boat', 'VVIP Boat'];
  readonly destinations = ['Alor', 'Banda Island', 'Komodo National Park', 'Raja Ampat'];
  private destroyed = new Subject<void>();
  constructor(private router: Router, private route: ActivatedRoute, private boatService: BoatService) {}

  ngOnInit(): void {
    this.route.queryParams.pipe(
      tap(params => {
        this.params = { ...params };
        delete this.params.departure_time;
        this.currentPage = Math.max(1, Number(params['page']) || 1);
        const array = (value: any): string[] => (Array.isArray(value) ? value : value ? [value] : []).map((v: string) => this.unslugify(v));
        this.selectedCategories = array(params['boat_kategori']);
        this.selectedDestinations = array(params['tujuan']);
        this.minPrice = params['min_price'] != null ? Number(params['min_price']) : null;
        this.maxPrice = params['max_price'] != null ? Number(params['max_price']) : null;
        this.sort = params['sort'] || 'recommended';
        const trips = array(params['trip_kategori']);
        this.activeTab = trips.length === 1 && trips[0] === 'Boat Charter' ? 'charter' : trips.length && trips.every(t => t.startsWith('Open Trip')) ? 'open' : 'all';
        this.loading = true;
        this.errorMessage = '';
        this.displayedBoats = [];
      }),
      switchMap(params => this.boatService.filterKapalLite({
        ...params,
        nama_kapal: params['keyword'] ?? params['nama_kapal']?.replace(/-/g, ' '),
        trip_kategori: params['trip_kategori'] ? (Array.isArray(params['trip_kategori']) ? params['trip_kategori'] : [params['trip_kategori']]).map((v: string) => this.unslugify(v)) : [],
        boat_kategori: this.selectedCategories,
        tujuan: this.selectedDestinations,
        page: this.currentPage, limit: this.pageSize
      }).pipe(catchError(error => {
        this.errorMessage = (error.status === 400 || error.error?.code === 'AVAILABILITY_UNVERIFIED') ? error.error?.message || 'Check your dates and filters, then search again.' : 'We couldn’t load the boats. Please try again.';
        return of({ items: [], totalItems: 0, totalPages: 0 });
      }))), takeUntil(this.destroyed)
    ).subscribe(response => {
      this.displayedBoats = response.items || [];
      this.totalItems = response.totalItems || 0;
      this.totalPages = response.totalPages || 0;
      this.loading = false;
    });
  }
  unslugify(value: string): string {
    return value.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }
  updateFilters(changes: any): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { ...this.params, ...changes, page: null } });
  }
  toggleFilter(key: string, value: string, checked: boolean): void {
    const selected = key === 'boat_kategori' ? this.selectedCategories : this.selectedDestinations;
    const next = checked ? [...selected, value] : selected.filter(item => item !== value);
    this.updateFilters({ [key]: next.length ? next : null });
  }
  setTab(tab: string): void {
    const trip = tab === 'charter' ? ['Boat Charter'] : tab === 'open' ? ['Open Trip Komodo National Park', 'Open Trip Outside Komodo National Park'] : null;
    this.updateFilters({ trip_kategori: trip });
  }
  applyPrice(): void {
    if ((this.minPrice != null && this.minPrice < 0) || (this.maxPrice != null && this.maxPrice < 0) || (this.minPrice != null && this.maxPrice != null && this.maxPrice < this.minPrice)) {
      this.errorMessage = 'Enter a valid price range with a maximum above the minimum.'; return;
    }
    this.updateFilters({ min_price: this.minPrice, max_price: this.maxPrice });
  }
  toggleFilters(): void {
    this.filtersOpen = !this.filtersOpen;
    if (this.filtersOpen) requestAnimationFrame(() => document.querySelector('.filter-sidebar')?.scrollIntoView({ block: 'start' }));
    else requestAnimationFrame(() => document.querySelector('.mobile-filter-button')?.scrollIntoView({ block: 'center' }));
  }
  clearFilters(): void {
    this.updateFilters({ boat_kategori: null, tujuan: null, min_price: null, max_price: null, trip_kategori: null });
  }
  changePage(page: number): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { ...this.params, page } });
    window.scrollTo({ top: 0, behavior: 'auto' });
  }
  retry(): void { this.router.navigate([], { relativeTo: this.route, queryParams: { ...this.params, retry: Date.now() } }); }
  getImageUrl(boat: any): string { const image = boat.ms_gambarkapals?.[0]?.gambar; return image ? getBoatImageUrl(image) : 'assets/img/placeholder.jpg'; }
  imageError(event: Event): void { const image = event.target as HTMLImageElement; image.onerror = null; image.src = 'assets/img/placeholder.jpg'; }
  price(boat: any): number { return Number(boat.trip_kategori === 'Open Trip Komodo National Park' ? boat.harga : boat.harga_display || boat.harga) || 0; }
  priceUnit(boat: any): string { return boat.trip_kategori === 'Boat Charter' ? '/ boat' : boat.durasi === 'Per Night' ? '/ night' : '/ person'; }
  tripLabel(boat: any): string { return boat.trip_kategori === 'Boat Charter' ? 'Private boat rental' : 'Open trip'; }
  destinationLabel(boat: any): string { return (boat.ms_tujuans || []).map((t: any) => t.tuj).join(' · '); }
  navigateToDetail(boat: any): void {
    const route = boat.trip_kategori === 'Boat Charter' ? '/detail-boat' : boat.trip_kategori === 'Open Trip Komodo National Park' ? '/detail-boatopen' : '/detail-boatoutside';
    this.router.navigate([route, boat.slug], { queryParams: { date_from: this.params.date_from, date_to: this.params.date_to } });
  }
  get rangeStart(): number { return this.totalItems ? (this.currentPage - 1) * this.pageSize + 1 : 0; }
  get rangeEnd(): number { return Math.min(this.currentPage * this.pageSize, this.totalItems); }
  ngOnDestroy(): void { this.destroyed.next(); this.destroyed.complete(); }
}
